import { computed, inject, resource } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  withHooks,
  patchState,
  withProps,
} from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  RolloutStateInterface,
  RolloutFormData,
  RolloutRequest,
  ErrorItem,
  RolloutStatusEntry,
  RolloutLogResponse,
} from './rollout.interface';
import { initialRolloutState } from './rollout.state';
import { RolloutStatus } from './rollout.const';
import { fromDatepicker } from '@shared/berlin-time';
import { addMilliseconds, isFirstDayOfMonth, isLastDayOfMonth } from 'date-fns';
import { RolloutDialogService } from '../rollout-dialog.service';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap, interval, takeWhile, map, from, Subscription } from 'rxjs';
import { ApiConfiguration, LogType } from '@swagger/humanresources';
import { getBranches, getRolloutStatusFromMilestone, extractRolloutErrors } from './rollout.helper';
import { isValidRolloutRange } from './rollout.utils';
import { ListResponseArgsOfAsyncTaskLogInfoDto } from 'src/swagger/humanresources/models/list-response-args-of-async-task-log-info-dto';
import { RolloutPollingSwService } from '../../../core/services/rollout-polling-sw.service';
import { AuthService } from '@core/auth';
import { RelativeDateTimeService } from '@shared/utils';

/**
 * Signal store for rollout dialog state management.
 * Follows the same patterns as WorktimeStore with NgRx Signals.
 */
/**
 * A range date from the dialog on the Berlin calendar: a picked day (device-local midnight)
 * becomes Berlin midnight of that day, a Berlin date (month select) stays as it is. Both
 * rollout requests then send the instants a Berlin user would send.
 */
const toBerlinRangeDate = (date: Date | null): Date | null => (date ? fromDatepicker(date) : null);

export const RolloutStore = signalStore(
  { providedIn: 'root' },
  withDevtools('rollout-store'),
  withState<RolloutStateInterface>(initialRolloutState),
  withProps(() => ({
    _rolloutDialogService: inject(RolloutDialogService),
    _swPollingService: inject(RolloutPollingSwService),
    _authService: inject(AuthService),
    _pollingSubscription: null as Subscription | null,
    _apiConfig: inject(ApiConfiguration),
    _relativeDateTimeService: inject(RelativeDateTimeService),
  })),
  withProps((store) => ({
    _asyncTaskResource: resource({
      params: () => ({ branches: store.branches() }),
      loader: async ({ params }) => {
        if (!params.branches || params.branches?.length > 0) {
          patchState(store, { loading: true });
          const res = await store._rolloutDialogService.fetchAsyncTasks(params.branches || []);
          patchState(store, { loading: false });
          return res;
        }
      },
    }),
    _asyncTaskLogsResource: resource({
      params: () => ({ logsUId: store.selectedReferenceLogsUId() }),
      loader: async ({ params }) => {
        if (!params.logsUId && params.logsUId !== null) {
          const res = await store._rolloutDialogService.fetchAsyncTaskLogs(params.logsUId);
          return res;
        }
      },
    }),
  })),
  withComputed((store) => ({
    asyncTaskResource: computed((): RolloutStatusEntry[] => {
      return store._asyncTaskResource.value();
    }),
    asyncTaskLogsResource: computed((): ListResponseArgsOfAsyncTaskLogInfoDto => {
      return store._asyncTaskLogsResource.value();
    }),
    /**
     * Computed signal that checks if form data is valid for rollout
     */
    isFormValid: computed(() => {
      const startDate = store.startDate();
      const endDate = store.endDate();
      const selectedBranch = store.selectedBranch();
      return (
        startDate !== null &&
        endDate !== null &&
        selectedBranch !== null &&
        selectedBranch.trim().length > 0 &&
        isValidRolloutRange(startDate, endDate)
      );
    }),

    /**
     * Computed signal that checks if form data is valid for contributor view rollout
     * In contributor view: selectedWeek is required, selectedBranch is not
     */
    isContributorFormValid: computed(() => {
      const startDate = store.startDate();
      const endDate = store.endDate();
      const selectedWeek = store.selectedWeek();

      return (
        startDate !== null &&
        endDate !== null &&
        selectedWeek !== null &&
        selectedWeek >= 1 &&
        selectedWeek <= 6 &&
        isValidRolloutRange(startDate, endDate)
      );
    }),

    /**
     * Computed signal for start month (if start date is first of month)
     */
    startMonth: computed(() => {
      const startDate = store.startDate();
      return startDate && isFirstDayOfMonth(startDate) ? startDate.getMonth() : null;
    }),

    /**
     * Computed signal for end month (if end date is last of month)
     */
    endMonth: computed(() => {
      const endDate = store.endDate();
      return endDate && isLastDayOfMonth(endDate) ? endDate.getMonth() : null;
    }),

    /**
     * Computed signal that returns rollout request data
     */
    rolloutRequestData: computed(() => {
      const startDate = store.startDate();
      const endDate = store.endDate();
      const selectedBranch = store.selectedBranch();

      if (!startDate || !endDate || !selectedBranch) return null;

      return {
        startDate,
        endDate,
        selectedBranch,
      } satisfies Omit<RolloutRequest, 'ouUId'>;
    }),

    /**
     * Computed signal that returns branch names as string array
     */
    branchNames: computed(() => {
      return (
        store.branches()?.map((branch) => {
          if (branch?.name) {
            return branch?.name;
          } else {
            console.warn('no name', branch);
            return 'undefined';
          }
        }) || []
      );
    }),

    /**
     * Computed signal that returns the count of available branches
     */
    branchesCount: computed(() => {
      return store.branches()?.length || 0;
    }),

    /**
     * Computed signal that returns the selected branch object
     */
    selectedBranchObject: computed(() => {
      const branches = store.branches();
      const selectedBranchUid = store.selectedBranchUid();

      if (!branches || !selectedBranchUid) {
        return null;
      }

      return branches.find((branch) => branch.uId === selectedBranchUid) || null;
    }),
  })),

  // Additional computed properties that depend on the first set
  withComputed((store) => ({
    /**
     * Computed signal that returns branches with updated lastRolloutStatus and referenceLogsUId
     * Performance optimized with Map lookup for O(1) complexity
     */
    branchesWithRolloutStatus: computed(() => {
      const branches = store.branches();
      const rolloutEntries = store.asyncTaskResource();
      if (!branches || !rolloutEntries || branches.length === 0 || rolloutEntries.length === 0) {
        return branches || [];
      }

      // Create Maps for O(1) lookups - performance optimized for 200+ branches
      const rolloutStatusMap = new Map<string, RolloutStatus>();
      const referenceLogsMap = new Map<string, string>();
      rolloutEntries.forEach((entry) => {
        rolloutStatusMap.set(entry.ouUId, entry.rolloutStatus);
        referenceLogsMap.set(entry.ouUId, entry.referenceLogsUId);
      });

      // Update branches with rollout status and referenceLogsUId - only creates new objects if data changed
      return branches.map((branch) => {
        const newRolloutStatus = rolloutStatusMap.get(branch.uId);
        const newReferenceLogsUId = referenceLogsMap.get(branch.uId);

        const statusChanged = newRolloutStatus && newRolloutStatus !== branch.lastRolloutStatus;
        const referenceLogsChanged =
          newReferenceLogsUId && newReferenceLogsUId !== branch.referenceLogsUId;

        if (statusChanged || referenceLogsChanged) {
          // Only create new object if any data actually changed to maintain reference equality
          return {
            ...branch,
            ...(newRolloutStatus && { lastRolloutStatus: newRolloutStatus }),
            ...(newReferenceLogsUId && { referenceLogsUId: newReferenceLogsUId }),
          };
        }

        // Return original branch object if no change needed
        return branch;
      });
    }),
  })),

  withMethods((store) => ({
    loadBranches: () => {
      const branches = getBranches();
      patchState(store, { branches });
    },
    /**
     * Updates the start date for rollout
     * @param startDate - The new start date
     */
    updateStartDate: (startDate: Date | null) => {
      patchState(store, {
        startDate: toBerlinRangeDate(startDate),
        error: null, // Clear error when updating dates
      });
    },

    /**
     * Updates the end date for rollout
     * @param endDate - The new end date
     */
    updateEndDate: (endDate: Date | null) => {
      patchState(store, {
        endDate: toBerlinRangeDate(endDate),
        error: null, // Clear error when updating dates
      });
    },

    /**
     * Updates the selected branch for rollout
     * @param selectedBranch - The new selected branch
     */
    updateSelectedBranch: (selectedBranch: string | null, uId: string) => {
      // const selectedBranchUid =
      //   store.branches()?.find((b) => b.data.name === selectedBranch)?.uId || null;
      const selectedBranchUid = uId;
      patchState(store, {
        selectedBranch,
        selectedBranchUid,
        error: null, // Clear error when updating branch
      });
    },

    /**
     * Updates the selected week for contributor view rollout
     * @param selectedWeek - The new selected week (1-6)
     */
    updateSelectedWeek: (selectedWeek: number | null) => {
      patchState(store, {
        selectedWeek,
        error: null, // Clear error when updating week
      });
    },

    /**
     * Updates the selected reference logs UID for detailed error viewing
     * @param selectedReferenceLogsUId - The reference logs UID to track
     */
    updateSelectedReferenceLogsUId: (selectedReferenceLogsUId: string | null) => {
      patchState(store, {
        selectedReferenceLogsUId,
        error: null, // Clear error when updating reference logs UID
      });
    },

    /**
     * Updates the rollout behavior setting
     * @param rolloutBehavior - What the rollout overwrites and what it keeps
     */
    updateRolloutBehavior: (rolloutBehavior: RolloutStateInterface['rolloutBehavior']) => {
      patchState(store, {
        rolloutBehavior,
        error: null, // Clear error when updating behavior
      });
    },

    /**
     * Updates both start and end dates from form data
     * @param formData - Form data containing both dates and selected branch
     */
    updateFormData: (formData: RolloutFormData) => {
      patchState(store, {
        startDate: toBerlinRangeDate(formData.startDate),
        endDate: toBerlinRangeDate(formData.endDate),
        selectedBranch: store.selectedBranch(),
        selectedWeek: formData.selectedWeek,
        error: null,
      });
    },
    /** Update valid weeks
     * @param validWeeks - Array of valid week strings
     */
    updateValidWeeks: (validWeeks: string[]) => {
      patchState(store, {
        validWeeks,
      });
    },

    /**
     * Sets loading state
     * @param loading - Loading state
     */
    setLoading: (loading: boolean) => {
      patchState(store, {
        loading,
        status: loading ? RolloutStatus.LOADING : RolloutStatus.IDLE,
      });
    },

    /**
     * Sets error state
     * @param error - Error message
     */
    setError: (error: ErrorItem[] | null) => {
      patchState(store, {
        error,
        status: error ? RolloutStatus.ERROR : RolloutStatus.IDLE,
        isError: true,
        loading: false,
      });
    },

    /**
     * Sets success state
     */
    setSuccess: () => {
      patchState(store, {
        status: RolloutStatus.SUCCESS,
        loading: false,
        error: null,
        isError: false,
      });
    },

    /**
     * Resets the store to initial state
     */
    reset: () => {
      // Stop any active polling

      const branches = store.branches();
      patchState(store, initialRolloutState);
      patchState(store, { branches }); // retain branches on reset
      store._asyncTaskResource.reload();
    },

    /**
     * Clears error state
     */
    clearError: () => {
      patchState(store, {
        error: [],
        status: RolloutStatus.IDLE,
      });
    },
    reload: () => {
      store._asyncTaskResource.reload();
    },

    /**
     * Extracts and patches error data from rollout log response
     * @param logResponse - The log response from fetchAsyncTaskLogs
     */
    extractAndPatchErrors: (logResponse: ListResponseArgsOfAsyncTaskLogInfoDto) => {
      try {
        const errors = extractRolloutErrors(logResponse);

        if (errors.length > 0) {
          patchState(store, {
            error: errors,
            isError: true,
            status: RolloutStatus.ERROR,
          });
        } else {
          // No errors found, clear error state
          patchState(store, {
            error: [],
            status: RolloutStatus.IDLE,
          });
        }
      } catch (error) {
        console.error('Failed to extract and patch rollout errors:', error);
        // Set a generic error state if extraction fails
        patchState(store, {
          error: [
            {
              employeeName: 'System',
              employeeId: 'N/A',
              errorMessage: 'Failed to process rollout error data',
            },
          ],
          isError: true,
          status: RolloutStatus.ERROR,
        });
      }
    },
  })),

  // Polling methods in separate withMethods block
  withMethods((store) => ({
    /**
     * Stop polling for async task logs
     */
    stopPolling: () => {
      if (store._pollingSubscription) {
        store._pollingSubscription.unsubscribe();
        store._pollingSubscription = null;
      }
    },
  })),

  // Polling start method that can access stopPolling
  withMethods((store) => ({
    /**
     * Start polling for async task logs
     * @param taskUId - The task UID to poll for
     */
    startPolling: (taskUId: string) => {
      // Stop any existing polling
      if (store._pollingSubscription) {
        store._pollingSubscription.unsubscribe();
        store._pollingSubscription = null;
      }

      // Start polling every 30 seconds
      store._pollingSubscription = interval(30000)
        .pipe(
          takeWhile(() => store.loading() === true), // Stop if loading becomes false
          switchMap(() => {
            return from(store._rolloutDialogService.fetchAsyncTaskLogs(taskUId));
          }),
          map((response) => {
            return response;
          }),
        )
        .subscribe({
          next: (response) => {
            // Check if we should stop polling based on LogType of the first element
            if (response?.result && Array.isArray(response.result) && response.result.length > 0) {
              const firstLog = response.result[0];
              const logType = firstLog.type;
              const hasTerminalState =
                logType === LogType.Succeeded || // 1
                logType === LogType.Failed || // 2
                logType === LogType.Aborted; // 4
              if (hasTerminalState) {
                patchState(store, {
                  loading: false,
                  selectedBranch: null,
                  selectedBranchUid: null,
                  status: getRolloutStatusFromMilestone(logType),
                });
                store.stopPolling();
              }
            }
          },
          error: (error) => {
            console.error('Polling error:', error);
            store.stopPolling();
          },
        });
    },
  })),

  // Separate withMethods block for RxJS methods that need access to polling methods
  withMethods((store) => ({
    branchRollout: rxMethod<void>(($data) =>
      $data.pipe(
        switchMap(() =>
          store._rolloutDialogService
            .rolloutOu(
              [store.selectedBranchUid()],
              store.startDate().toISOString(),
              addMilliseconds(store.endDate(), 1).toISOString(),
              store.rolloutBehavior(),
            )
            .pipe(
              tapResponse({
                next: async (response) => {
                  // Get branch info for SW polling
                  const selectedBranch = store.selectedBranchObject();
                  if (!selectedBranch) {
                    console.error('[Store] No selected branch found for SW polling');
                    return;
                  }
                  const baseUrl = store._apiConfig.rootUrl;
                  // Start Service Worker polling immediately
                  const authToken = store._authService.getToken();
                  const pollingConfig = {
                    rolloutId: `rollout_${store._relativeDateTimeService.getDateNow()}_${Math.random().toString(36).substr(2, 9)}`,
                    asyncTaskUId: response.result.uId,
                    branchUid: selectedBranch.uId,
                    branchName: selectedBranch.name,
                    apiConfig: {
                      endpoint: `${baseUrl}/user/me/asynctask/${response.result.uId}/logs`,
                      headers: {
                        'Content-Type': 'application/json',
                        ...(authToken && { Authorization: `Bearer ${authToken}` }),
                      },
                      queryParams: {
                        onlyMilestones: 'true',
                      },
                    },
                  };

                  // Start SW polling
                  store._swPollingService.startPolling(pollingConfig).subscribe({
                    next: (_started) => {
                      // Polling started successfully
                    },
                    error: (error) => {
                      console.error('[Store] Service Worker polling failed:', error);
                    },
                  });

                  // Set state to BACKGROUND (not loading locally)
                  patchState(store, {
                    status: RolloutStatus.BACKGROUND,
                    // loading: false, // No local loading since SW handles it
                    error: null,
                    isError: false,
                  });
                },
                error: (error) => {
                  console.warn('Rollout error:', error);
                  // Stop polling on error
                  store.stopPolling();

                  patchState(store, {
                    error: [error as ErrorItem],
                    status: error ? RolloutStatus.ERROR : RolloutStatus.IDLE,
                    isError: true,
                    loading: false,
                  });
                },
              }),
            ),
        ),
      ),
    ),
    contributorRollout: rxMethod<{
      contributorUId: string;
      scheduleUId: string;
      workScheduleUid: string;
    }>(($data) =>
      $data.pipe(
        switchMap(({ contributorUId, scheduleUId, workScheduleUid }) => {
          patchState(store, { error: null, isError: false });
          return store._rolloutDialogService
            .rolloutContributor(
              contributorUId,
              scheduleUId,
              workScheduleUid,
              store.selectedWeek() - 1 || 0,
              store.startDate().toISOString(),
              addMilliseconds(store.endDate(), 1).toISOString(),
              store.rolloutBehavior(),
            )
            .pipe(
              tapResponse({
                next: async (_response) => {
                  patchState(store, {
                    status: RolloutStatus.SUCCESS,
                    loading: false,
                    error: null,
                    isError: false,
                  });
                },
                error: (error) => {
                  patchState(store, {
                    error: [error as ErrorItem],
                    status: RolloutStatus.ERROR,
                    isError: true,
                    loading: false,
                  });
                },
              }),
            );
        }),
      ),
    ),
    /**
     * Updates the contributor view state
     * @param isContributorView - True if in contributor view, false otherwise
     */
    updateIsContributorView: (isContributorView: boolean) => {
      patchState(store, {
        isContributorView,
      });
    },

    /**
     * Updates the rollout status for a single branch by its uId
     * This is a simple method that finds the specific branch and updates only its status
     * @param branchUId - The unique identifier of the branch to update
     * @param newStatus - The new rollout status to set
     */
    updateBranchRolloutStatus: (branchUId: string, newStatus: RolloutStatus) => {
      const currentEntries = store._asyncTaskResource.value();
      if (!currentEntries || currentEntries.length === 0) {
        console.warn('[RolloutStore] No rollout entries found to update');
        return;
      }

      // Find and update only the entry with matching ouUId
      const updatedEntries = currentEntries.map((entry) => {
        if (entry.ouUId === branchUId) {
          return {
            ...entry,
            rolloutStatus: newStatus,
            changed: store._relativeDateTimeService.getNewDateToISOString(), // Update the changed timestamp
          };
        }
        return entry; // Return unchanged for other entries
      });

      // Check if we actually found and updated an entry
      const wasUpdated = updatedEntries.some((entry, index) => entry !== currentEntries[index]);

      if (wasUpdated) {
        // Update the resource with the new entries
        store._asyncTaskResource.set(updatedEntries);
      } else {
        console.warn(`[RolloutStore] Branch with uId ${branchUId} not found in rollout entries`);
      }
    },

    /**
     * Updates branch rollout status and extracts error data from completion event
     * @param branchUId - The unique identifier of the branch to update
     * @param newStatus - The new rollout status to set
     * @param logResponse - Optional log response containing error details
     */
    updateBranchRolloutStatusWithErrors: (
      branchUId: string,
      newStatus: RolloutStatus,
      logResponse?: RolloutLogResponse,
    ) => {
      const currentEntries = store._asyncTaskResource.value();
      if (!currentEntries || currentEntries.length === 0) {
        console.warn('[RolloutStore] No rollout entries found to update');
        return;
      }

      // Find and update only the entry with matching ouUId
      const updatedEntries = currentEntries.map((entry) => {
        if (entry.ouUId === branchUId) {
          const updatedEntry = {
            ...entry,
            rolloutStatus: newStatus,
            changed: store._relativeDateTimeService.getNewDateToISOString(), // Update the changed timestamp
          };

          // Add logResponse if provided
          if (logResponse) {
            updatedEntry.logResponse = logResponse;
          }

          return updatedEntry;
        }
        return entry; // Return unchanged for other entries
      });

      // Check if we actually found and updated an entry
      const wasUpdated = updatedEntries.some((entry, index) => entry !== currentEntries[index]);

      if (wasUpdated) {
        // Update the resource with the new entries
        store._asyncTaskResource.set(updatedEntries);

        // If this is an error or warning status and we have logResponse, extract and store errors
        // BUT don't set isError: true (that should only happen when user explicitly views errors)
        if (
          (newStatus === RolloutStatus.ERROR || newStatus === RolloutStatus.WARNING) &&
          logResponse
        ) {
          try {
            const errors = extractRolloutErrors({
              result: [{ jsonMessage: JSON.stringify(logResponse) }],
            });
            if (errors.length > 0) {
              // Store error data but don't automatically show error view
              patchState(store, {
                error: errors,
                // Don't set isError: true - let user click to view errors
              });
            }
          } catch (error) {
            console.warn('[RolloutStore] Failed to extract errors from logResponse:', error);
          }
        }
      } else {
        console.warn(`[RolloutStore] Branch with uId ${branchUId} not found in rollout entries`);
      }
    },

    /**
     * Initialize the store with date constraints for contributor rollout
     * @param minStartDate - The earliest allowed start date
     * @param maxEndDate - The latest allowed end date
     */
    initializeWithDateConstraints: (minStartDate: Date | null, maxEndDate: Date | null) => {
      patchState(store, {
        minStartDate,
        maxEndDate,
      });
    },
  })),

  withHooks((store) => ({
    onInit() {
      // Load branches on store init
      store.reset();
    },
    /**
     * Resets store state when store is destroyed
     * This provides automatic cleanup without requiring manual ngOnDestroy in components
     */
    onDestroy() {
      store.reset();
    },
  })),
);
